import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Add as AddIcon, Person as PersonIcon } from '@mui/icons-material';

interface CreateStaffFormData {
  name: string;
  email: string;
  role: 'developer' | 'project_manager' | '';
  phone: string;
  generatePassword: boolean;
  sendWelcomeEmail: boolean;
  password: string;
  passwordConfirmation: string;
}

interface CreateStaffMemberProps {
  onStaffCreated?: () => void;
}

/**
 * Create Staff Member Component
 * Admin interface for creating developers and project managers
 */
const CreateStaffMember: React.FC<CreateStaffMemberProps> = ({ onStaffCreated }) => {
  const [formData, setFormData] = useState<CreateStaffFormData>({
    name: '',
    email: '',
    role: '',
    phone: '',
    generatePassword: true,
    sendWelcomeEmail: true,
    password: '',
    passwordConfirmation: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [generatedPassword, setGeneratedPassword] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setGeneratedPassword('');

    // Basic validation
    if (!formData.name || !formData.email || !formData.role) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (!formData.generatePassword && (!formData.password || formData.password !== formData.passwordConfirmation)) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const requestData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        phone: formData.phone,
        generate_password: formData.generatePassword,
        send_welcome_email: formData.sendWelcomeEmail,
        ...(formData.generatePassword ? {} : {
          password: formData.password,
          password_confirmation: formData.passwordConfirmation
        })
      };

      // API call to create staff member
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${API_BASE_URL}/admin/staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(`${formData.role.replace('_', ' ')} created successfully!`);
        if (result.temporary_password) {
          setGeneratedPassword(result.temporary_password);
        }
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          role: '',
          phone: '',
          generatePassword: true,
          sendWelcomeEmail: true,
          password: '',
          passwordConfirmation: ''
        });

        // Notify parent component
        if (onStaffCreated) {
          onStaffCreated();
        }
      } else {
        setError(result.message || 'Failed to create staff member');
      }
    } catch (err) {
      setError('Failed to create staff member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon />
            <Typography variant="h6">Create Staff Member</Typography>
          </Box>
        }
        subheader="Add new developers or project managers to the team"
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
        
        {generatedPassword && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Temporary Password Generated:
            </Typography>
            <Typography variant="body2" fontFamily="monospace" sx={{ backgroundColor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 1 }}>
              {generatedPassword}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Please store this password securely and share it with the new team member.
            </Typography>
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </Box>

          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 3 }}>
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'developer' | 'project_manager' })}
                label="Role"
              >
                <MenuItem value="developer">Developer</MenuItem>
                <MenuItem value="project_manager">Project Manager</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </Box>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>
            Password Settings
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="generatePassword"
                  checked={formData.generatePassword}
                  onChange={(e) => setFormData({ ...formData, generatePassword: e.target.checked })}
                />
              }
              label="Generate temporary password automatically"
            />
          </Box>

          {!formData.generatePassword && (
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!formData.generatePassword}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                name="passwordConfirmation"
                type="password"
                value={formData.passwordConfirmation}
                onChange={(e) => setFormData({ ...formData, passwordConfirmation: e.target.value })}
                required={!formData.generatePassword}
              />
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="sendWelcomeEmail"
                  checked={formData.sendWelcomeEmail}
                  onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
                />
              }
              label="Send welcome email with login instructions"
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Creating...' : `Create ${formData.role?.replace('_', ' ') || 'Staff Member'}`}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CreateStaffMember;